// Inline SVG sprite for icons that repeat many times in one document.
//
// THE PROBLEM
// -----------
// SVGR renders every icon as a self-contained <svg> with full path data. On
// /apartments that meant 508 icon instances carrying only 18 distinct icons —
// 411 KB of inline SVG that was ~95% duplicate bytes. Three components produce
// most of it: Tag (a badge icon AND a chevron, per tag, ~142 tags), Rating
// (a star per card) and SaveButton (a heart per card).
//
// THE FIX
// -------
// <IconSprite /> emits each icon's geometry once as a <symbol>; <SpriteIcon />
// references it with a ~70 byte <use>. Geometry comes from iconSpriteData.ts,
// generated from the same source SVGs and the same SVGO config the SVGR build
// uses, so a symbol and its <IconFoo /> equivalent are identical.
//
// WHERE THE SPRITE LIVES
// ----------------------
// The root layout, NOT the /apartments layout. The components that consume
// <SpriteIcon> (Tag, Rating, SaveButton) render across the whole app, so a
// route-scoped sprite would leave <use> pointing at a missing symbol — and an
// invisible icon — on every other route. A fixed ~12 KB in the root layout
// buys the saving everywhere instead.
//
// ADDING AN ICON
// --------------
// Add it to SPRITE_ICONS in scripts/generate-icon-sprite.mjs and re-run the
// script. Only add icons that actually repeat: a symbol referenced once costs
// slightly MORE than inlining it. `SpriteIconName` is a union of the generated
// ids, so referencing a symbol that doesn't exist is a type error rather than a
// blank space on the page.

import { ICON_SYMBOLS, ICON_VIEWBOX, type SpriteIconName } from './iconSpriteData';

/** Prefix keeps sprite ids from colliding with ids inside other inline SVGs. */
const PREFIX = 'oi-';

/**
 * Emits the <symbol> definitions. Render exactly once per document, in the
 * root layout, before the content that references it.
 *
 * `display:none` on the container is deliberate: <symbol> content is never
 * painted where it is declared, and hiding the wrapper keeps it out of layout
 * entirely. aria-hidden + focusable="false" keep it out of the a11y tree and
 * the tab order.
 */
export function IconSprite() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'none' }}
      aria-hidden="true"
      focusable="false"
      data-icon-sprite=""
    >
      {ICON_SYMBOLS.map(symbol => (
        <symbol
          key={symbol.id}
          id={`${PREFIX}${symbol.id}`}
          viewBox={symbol.viewBox}
          dangerouslySetInnerHTML={{ __html: symbol.body }}
        />
      ))}
    </svg>
  );
}

interface SpriteIconProps {
  name: SpriteIconName;
  className?: string;
  /**
   * Accessible name. Omit for decorative icons (the default) — the icon is
   * then aria-hidden so screen readers skip it, which is what you want when
   * adjacent text already conveys the meaning.
   */
  title?: string;
}

/**
 * Drop-in replacement for an `<IconFoo />` from @repo/svgs.
 *
 * The wrapper <svg> repeats the props SVGR applies via `svgProps` in
 * .svgrrc.web.cjs (role/focusable/width/height) plus the viewBox, because
 * <use> does not inherit a viewBox from the referenced <symbol>.
 */
export function SpriteIcon({ name, className, title }: SpriteIconProps) {
  return (
    <svg
      viewBox={ICON_VIEWBOX[name]}
      fill="none"
      role="img"
      focusable="false"
      width="100%"
      height="100%"
      className={className}
      {...(title ? { 'aria-label': title } : { 'aria-hidden': true })}
    >
      {title ? <title>{title}</title> : null}
      <use href={`#${PREFIX}${name}`} />
    </svg>
  );
}
